/** Fold tab: the Fold (IPM's galaxy sell) and fleet stats. The Challenge and Matrix sections render in the Station. */

import { FOLD_MIN_GALAXY_VALUE } from '../../content/planets';
import { FLEET_STAT_LABEL, type FleetStat } from '../../content/resources';
import { MATRIX_NODES } from '../../content/matrix';
import { driftDef } from '../../content/drifts';
import { ADDITIONS } from '../../content/flags';
import { allocatedNodes } from '../../sim/actions';
import { CHALLENGE_HOURS, CHALLENGE_TIERS, CHALLENGE_UNLOCK_FOLDS, canStartChallenge, challengeRun, challengeUnlocked, challengeVs } from '../../sim/challenges';
import { baseCredits, canFold, distillTe, foldSc, foldTe } from '../../sim/fold';
import { cur, esc, fmtNum, fmtSigned, fmtTime, price } from '../format';
import { renderMatrixMap } from '../matrixMap';
import { btn, type View } from '../view';

function pendingView(v: View): string {
  const offer = v.state.pendingFold!;
  const { ui } = v;
  const drifts = offer.drifts.map((id) => {
    const d = driftDef(id)!;
    return `<button class="vm-card${ui.foldDrift === id ? ' picked' : ''}" data-act="pickDrift" data-id="${id}">
      <h4 style="color:${d.color}">${esc(d.name)}</h4>
      <div class="up">▲ ${esc(d.upside)}</div>
      <div class="down">▼ ${esc(d.downside)}</div>
    </button>`;
  }).join('');
  const choose = offer.drifts.length > 0;
  return `<section class="vm-section vm-glow" data-key="pending">
    <div class="vm-section-head gold">Space is folding</div>
    <div class="vm-chipline" style="margin-bottom:8px"><span>${price('te', offer.te + offer.distilled)}</span><span>${price('sc', offer.sc)}</span></div>
    ${choose ? `<div class="vm-section-head cyan">Choose the next Drift</div><div class="vm-cards">${drifts}</div>` : '<p class="vm-note">Sell this galaxy and dock at the Station to spend what it paid. The next galaxy starts at ◈100; everything permanent stays.</p>'}
    <div style="margin-top:10px">${btn(!choose || ui.foldDrift ? 'Fold space' : 'Pick a Drift to fold', 'completeFold', { cls: 'hero wide', disabled: choose && !ui.foldDrift })}</div>
  </section>`;
}

/** The next whole order of magnitude: IPM pays best at exactly $10M, $100M, $1B… */
function nextMilestone(value: number): number {
  return Math.pow(10, Math.max(7, Math.floor(Math.log10(Math.max(1, value))) + 1));
}

function previewView(v: View): string {
  const { state } = v;
  const value = state.run.galaxyValue;
  const te = foldTe(state);
  const distilled = distillTe(state);
  const ok = canFold(state);
  const drift = ADDITIONS.drifts ? driftDef(state.run.drift) : null;
  const target = ok ? nextMilestone(value) : FOLD_MIN_GALAXY_VALUE;
  const pct = Math.min(100, (100 * value) / target);
  return `<section class="vm-section" data-key="preview">
    <div class="vm-section-head gold">The Fold <span class="aside">Fold ${state.folds + 1}${drift ? ` · now <span style="color:${drift.color}">${esc(drift.name)}</span>` : ''}</span></div>
    <p class="vm-note">Sell this galaxy for TE. <b>Galaxy Value</b> is everything spent on planets and upgrades. Planets, GC, projects, recipes, colonies and the hold reset; Rooms, ships, crew and the Matrix stay.</p>
    <div class="vm-stats">
      <div><span>Galaxy Value</span><b>${price('gc', value)}</b></div>
      <div><span>TE (rooms)</span><b>${price('te', te + distilled)}</b></div>
    </div>
    <div class="vm-bar gold"><i style="width:${pct.toFixed(1)}%"></i></div>
    <p class="vm-note vm-faint" style="margin-top:6px">${ok
      ? `${fmtNum(baseCredits(target))} base TE at ${price('gc', target)}. Folding just past a power of ten pays best.`
      : `Folding opens at ${price('gc', FOLD_MIN_GALAXY_VALUE)} Galaxy Value.`} Also pays ${price('sc', foldSc(state))}.</p>
    ${btn(ok ? `Fold · +${fmtNum(te + distilled)} TE` : `Reach ${price('gc', FOLD_MIN_GALAXY_VALUE)} to Fold`, 'beginFold', { cls: 'hero wide', disabled: !ok })}
  </section>`;
}

/** Challenges: the Matrix's VS source until online tournaments (GDD §5.6). */
export function challengeSection(v: View): string {
  const { state } = v;
  const tiers = CHALLENGE_TIERS.map(([value, vs]) => `<span>${price('gc', value)} → ${price('vs', vs)}</span>`).join('');
  const head = `<div class="vm-section-head" style="color:var(--magenta)">Challenge <span class="aside">${cur('vs')} for the Matrix</span></div>`;
  if (!challengeUnlocked(state)) {
    return `<section class="vm-section" data-key="challenge">${head}
      <p class="vm-note">How big a galaxy can you build in ${CHALLENGE_HOURS} hours? It runs alongside your main galaxy and pays Void Shards, the only way to grow the Matrix until online tournaments arrive.</p>
      <p class="vm-note"><b>Unlocks after your ${CHALLENGE_UNLOCK_FOLDS === 2 ? 'second' : `${CHALLENGE_UNLOCK_FOLDS}th`} Fold</b> (${state.folds}/${CHALLENGE_UNLOCK_FOLDS}).</p>
      <div class="vm-chipline">${tiers}</div></section>`;
  }
  const run = challengeRun(state);
  if (run && state.challenge) {
    const left = Math.max(0, state.challenge.endsAt - state.clock);
    const pct = Math.min(100, (100 * (state.clock - state.challenge.startedAt)) / (CHALLENGE_HOURS * 3600));
    const viewing = state.challenge.viewing === 'challenge';
    return `<section class="vm-section vm-glow" data-key="challenge">${head}
      <div class="vm-stats">
        <div><span>Galaxy Value</span><b>${price('gc', run.galaxyValue)}</b></div>
        <div><span>Pays now</span><b>${price('vs', challengeVs(run.galaxyValue))}</b></div>
      </div>
      <div class="vm-bar magenta"><i style="width:${pct.toFixed(1)}%"></i></div>
      <p class="vm-note" style="margin-top:6px">${fmtTime(left)} left. Both galaxies keep running; switch between them at the top of the screen.</p>
      <div class="vm-chipline" style="margin-bottom:8px">${tiers}</div>
      <div class="vm-btns" style="justify-content:flex-start">
        ${btn(viewing ? 'Back to main galaxy' : 'Play the Challenge', 'viewGalaxy', { cls: 'magenta', data: { to: viewing ? 'main' : 'challenge' } })}
        ${btn('End now and collect', 'endChallenge', { cls: 'ghost' })}
      </div></section>`;
  }
  const ready = canStartChallenge(state);
  const wait = Math.max(0, state.challengeReadyAt - state.clock);
  return `<section class="vm-section" data-key="challenge">${head}
    <p class="vm-note">Start a fresh galaxy that runs for ${CHALLENGE_HOURS} hours beside your main one. Your Rooms, Matrix and crew all help. The bigger it grows, the more VS it pays.</p>
    <div class="vm-chipline" style="margin-bottom:8px">${tiers}</div>
    ${btn(ready ? 'Start a Challenge' : `Next Challenge in ${fmtTime(wait)}`, 'startChallenge', { cls: 'magenta wide', disabled: !ready })}
    ${state.challengesDone ? `<p class="vm-note vm-faint" style="margin-top:6px">${state.challengesDone} done · best ${price('gc', state.bestChallenge)}</p>` : ''}
  </section>`;
}

export function matrixSection(v: View): string {
  const { state } = v;
  return `<section class="vm-section" data-key="matrix">
    <div class="vm-section-head" style="color:var(--magenta)">Matrix <span class="aside">${price('vs', state.vs)} · ${allocatedNodes(state)}/${MATRIX_NODES.length - 1} nodes · ${Object.values(state.matrix).reduce((a, b) => a + b, 0)} levels</span></div>
    <div class="vm-mx">
      ${renderMatrixMap(v)}
      <div class="vm-mx-tools">
        ${btn('+', 'mxZoom', { cls: 'ghost', data: { dir: 'in' } })}
        ${btn('−', 'mxZoom', { cls: 'ghost', data: { dir: 'out' } })}
        ${btn('⌖', 'mxZoom', { cls: 'ghost', data: { dir: 'home' } })}
      </div>
    </div>
    <p class="vm-note vm-faint" style="margin-top:6px">Permanent bonuses; most nodes take several levels. Drag to pan · pinch or +/− to zoom · tap a node. Refunds are free.</p>
  </section>`;
}

function statsView(v: View): string {
  const entries = (Object.keys(FLEET_STAT_LABEL) as FleetStat[]).filter((s) => Math.abs(v.stats[s]) > 1e-9);
  const rows = entries.map((s) => `<dt>${FLEET_STAT_LABEL[s]}</dt><dd>${fmtSigned(v.stats[s])}</dd>`).join('');
  return `<section class="vm-section" data-key="stats">
    <div class="vm-section-head cyan">Fleet bonuses <span class="aside">projects · Matrix${ADDITIONS.fittings ? ' · fittings' : ''}${ADDITIONS.drifts ? ' · Drift' : ''}</span></div>
    ${rows ? `<dl class="vm-kv">${rows}</dl>` : '<p class="vm-note">None yet.</p>'}
    <p class="vm-note vm-faint" style="margin-top:8px">Folds ${v.state.folds} · lifetime ${price('gc', v.state.lifetime.gc)} · ${v.state.lifetime.crafted} items crafted${ADDITIONS.contracts ? ` · ${v.state.lifetime.contracts} contracts` : ''}</p>
  </section>`;
}

function devView(v: View): string {
  if (!import.meta.env.DEV) return '';
  return `<section class="vm-section" data-key="dev" style="border-color:rgba(245,197,66,0.4)">
    <div class="vm-section-head gold">Dev <span class="aside">dev server only · speed ×${v.ui.devSpeed}</span></div>
    <div class="vm-btns" style="justify-content:flex-start">
      ${btn('+1M GC', 'dev', { cls: 'ghost', data: { what: 'gc' } })}
      ${btn('×1000 GC', 'dev', { cls: 'ghost', data: { what: 'gcx' } })}
      ${btn('+10M value', 'dev', { cls: 'ghost', data: { what: 'value' } })}
      ${btn('Ore pile', 'dev', { cls: 'ghost', data: { what: 'ores' } })}
      ${btn('+100 TE', 'dev', { cls: 'ghost', data: { what: 'te' } })}
      ${btn('+100 SC', 'dev', { cls: 'ghost', data: { what: 'sc' } })}
      ${btn('+100 VS', 'dev', { cls: 'ghost', data: { what: 'vs' } })}
      ${btn('+2 Folds', 'dev', { cls: 'ghost', data: { what: 'folds' } })}
      ${btn('Grant store ships', 'dev', { cls: 'ghost', data: { what: 'storeShips' } })}
      ${btn('End Challenge clock', 'dev', { cls: 'ghost', data: { what: 'challengeEnd' } })}
      ${btn('+1h offline', 'dev', { cls: 'ghost', data: { what: 'offline' } })}
      ${btn('Wipe save', 'dev', { cls: 'magenta', data: { what: 'wipe' } })}
    </div>
  </section>`;
}

function challengeFoldNote(): string {
  return `<section class="vm-section" data-key="preview">
    <div class="vm-section-head" style="color:var(--magenta)">Challenge galaxy</div>
    <p class="vm-note">This galaxy can't Fold. It ends on its own after ${CHALLENGE_HOURS} hours and pays Void Shards; switch to your main galaxy to Fold.</p>
    ${btn('Back to main galaxy', 'viewGalaxy', { cls: 'ghost wide', data: { to: 'main' } })}
  </section>`;
}

export function renderFold(v: View): string {
  const top = v.state.run.challenge ? challengeFoldNote() : v.state.pendingFold ? pendingView(v) : previewView(v);
  return top + statsView(v) + devView(v);
}
